import {
  CustomFieldFormats,
  CustomFieldTypes,
  type PageConfigurationRuleGroup,
  TransferType,
} from '@generated/graphql';
import type { AlternativeAddress } from '@interfaces/alternativeAddress';
import type { BankAccount } from '@interfaces/bankAccount';
import type { CustomFieldValue } from '@interfaces/customField';
import type { PhysicalAddress } from '@interfaces/physicalAddress';
import type { TranslatedString } from '@interfaces/shared';
import type { Holding } from '@interfaces/stats';
import type { ScheduledTransfer } from '@interfaces/transfer';
import type { User } from '@interfaces/user';
import type { ValidateRule } from '@interfaces/validateRule';
import type { CountryCodes, LanguageCodes } from '@onevesthq/ov-enums';
import type { Maybe } from 'graphql/jsutils/Maybe';
import type { TFunction } from 'i18next';
import jp from 'jsonpath';
import parsePhoneNumberFromString, {
  type CountryCode,
  parsePhoneNumber,
} from 'libphonenumber-js';
import { isNull, round } from 'lodash-es';
import { getLocaleString, hasAnyTranslation } from '../assets/i18n/config';
import type { BookValueType } from '../providers/statsHooks';
import { dollars2cents } from './dollars2cents';
import { formatMoneyValue, isCurrencySymbolAfter } from './moneyFormatting';

export const parseJwt = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join(''),
    );

    return JSON.parse(jsonPayload);
  } catch {
    return {};
  }
};

export const delay = (ms: number): Promise<void> =>
  new Promise((res) => setTimeout(res, ms));

export const getLocaleNumberSeparators = () => {
  const localeParts = new Intl.NumberFormat(getLocaleString()).formatToParts(
    12345.6,
  );

  return {
    thousandSeparator:
      localeParts.find((part) => part.type === 'group')?.value ?? ',',
    decimalSeparator:
      localeParts.find((part) => part.type === 'decimal')?.value ?? '.',
  };
};

export const getDecimalPlaceholder = (decimalPlaces: number): string => {
  if (!decimalPlaces) return '0';

  const { decimalSeparator } = getLocaleNumberSeparators();
  return `0${decimalSeparator}${'0'.repeat(decimalPlaces)}`;
};

/**
 * Converts text typed into a decimal input to the canonical dot form ("12.5").
 * Accepts the locale decimal separator and '.'. Returns null for text that is
 * not a valid (possibly partial) decimal, such as letters or grouping separators.
 */
export const normalizeDecimalInput = (
  raw: string,
  decimalSeparator: string,
): string | null => {
  const match = /^(-?\d*)([.,]?)(\d*)$/.exec(raw.trim());
  if (!match) return null;

  const [, whole, separator, fraction] = match;
  if (separator && separator !== '.' && separator !== decimalSeparator) {
    return null;
  }

  // A separator with no digits around it is not a number yet, so drop it.
  // Otherwise "." reaches the caller and parses as NaN.
  if (separator && !fraction && !/\d/.test(whole)) {
    return whole;
  }

  return separator ? `${whole}.${fraction}` : whole;
};

type TranslatedName =
  | TranslatedString
  | Partial<Record<LanguageCodes, string | null | undefined>>
  | null
  | undefined;

/**
 * Downloads data as a JSON file
 * @param data - The data to download (will be stringified)
 * @param filename - The filename (without .json extension). Can be a string or a translated name object.
 * @param fallback - Fallback filename if the translated name is empty (default: 'download')
 */
export const downloadAsJson = (
  translateBackendString: (translatedObject: unknown) => string,
  data: unknown,
  filename: string | TranslatedName,
  fallback = 'download',
): void => {
  const resolvedFilename =
    typeof filename === 'string'
      ? filename
      : translateBackendString(filename) || fallback;

  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${resolvedFilename}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const formatAmountValueCents = (
  cents: number,
  fractionDigits = 2,
): string => {
  const locale = getLocaleString();
  const value = (Math.round(cents) || 0) / 100;
  const absoluteValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (Number.isInteger(value)) {
    return `${sign}${new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(absoluteValue)}`;
  }

  return `${sign}${new Intl.NumberFormat(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(absoluteValue)}`;
};

export const formatDecimalMoneyValue = (
  cents?: number,
  decimalPlaces = 4,
  currency = '$',
): string => {
  const locale = getLocaleString();
  const symbolAfter = isCurrencySymbolAfter(locale);

  if (!cents) {
    return symbolAfter ? `0 ${currency}` : `${currency}0`;
  }

  const absValue = Math.abs(round(cents / 100, decimalPlaces));
  const sign = cents < 0 ? '-' : '';
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimalPlaces,
  }).format(absValue);

  return symbolAfter
    ? `${sign}${formatted} ${currency}`
    : `${sign}${currency}${formatted}`;
};

export const formatDecimalNumber = (
  number?: number,
  decimalPlaces?: number,
  returnEmptyString = false,
): string => {
  if (!number) return returnEmptyString ? '' : '0';

  const locale = getLocaleString();
  const roundedNumber = decimalPlaces ? round(number, decimalPlaces) : number;

  // When fraction digit bounds are omitted, Intl defaults to max 3 fractional digits
  // (e.g. 0.0005 displays as 0.001). Quantity-style values need finer precision.
  if (decimalPlaces === undefined) {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 6,
    }).format(roundedNumber);
  }

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(roundedNumber);
};

export const generateAddressString = (
  address: Maybe<Partial<PhysicalAddress>>,
): string => {
  if (!address) return '';

  const numbersSeparatpr = address.unitNumber && address.houseNumber ? '-' : '';
  return [
    [
      address.unitNumber,
      numbersSeparatpr,
      address.houseNumber,
      address.streetName,
    ]
      .join(' ')
      .trim(),
    address.city,
    address.jurisdiction
      ? address.jurisdiction.substring(3, 5)
      : address.province,
    address.postal,
    countryName(address.country || ''),
  ]
    .filter(Boolean)
    .join(', ');
};

export const generateAlternativeAddressString = (
  address: AlternativeAddress,
): string =>
  [
    address.line1,
    address.line2,
    address.city,
    address.jurisdiction,
    countryName(address.country),
    address.postal,
  ]
    .filter(Boolean)
    .join(', ');

export const generateShortAddressString = (
  address?: PhysicalAddress,
): string => {
  if (!address) return '';

  return [
    address.city,
    address.jurisdiction
      ? address.jurisdiction.substring(3, 5)
      : address.province,
    countryName(address.country || 'CA'),
  ]
    .filter(Boolean)
    .join(', ');
};

function countryName(country: string): string {
  if (country === 'CA') return 'Canada';
  if (country === 'US') return 'USA';
  return country;
}

export const generateBankDetailString = (
  bankAccount: Pick<
    BankAccount,
    'name' | 'institutionNumber' | 'bankAccountNumber'
  >,
): string => {
  const bankAccountName = bankAccount?.name ? `${bankAccount.name} ` : '';
  const bankAccountInstitutionNumber = bankAccount?.institutionNumber ?? '';
  const bankAccountNumber = bankAccount?.bankAccountNumber ?? '';

  const separator =
    bankAccount?.institutionNumber && bankAccount?.bankAccountNumber ? '-' : '';
  return `${bankAccountName}${bankAccountInstitutionNumber}${separator}${bankAccountNumber}`;
};

export const generateSourceText = (
  t: TFunction,
  transfer: ScheduledTransfer,
): string => {
  if (transfer?.toSubAccount) {
    const accType = t(`accountTypes:${transfer?.toSubAccount?.account?.type}`);
    return t('transferTo', { subAccount: accType });
  }
  return '';
};

export const generateTransferSourceText = (
  t: TFunction,
  transfer: ScheduledTransfer,
): string =>
  `${generateSourceText(t, transfer)} ${transfer.bankAccount ? generateBankDetailString(transfer.bankAccount) : ''}`;

export type TransferTypeLabelTransfer = {
  type?: string;
  isWithdrawAll?: boolean | null;
};

export type GetTransferTypeLabelOptions = {
  typeKeyPrefix?: 'components:pendingTransfers.types' | 'transfer:types';
};

export const getTransferTypeLabel = (
  t: TFunction,
  transfer: TransferTypeLabelTransfer,
  options?: GetTransferTypeLabelOptions,
): string => {
  const typeKeyPrefix =
    options?.typeKeyPrefix ?? 'components:pendingTransfers.types';

  if (transfer.isWithdrawAll && transfer.type === TransferType.Withdraw) {
    return t('components:transferModal.withdrawAll');
  }

  if (!transfer.type) {
    return '';
  }

  return t(`${typeKeyPrefix}.${transfer.type}`);
};

export const generateClientInitials = (user: User | undefined): string => {
  if (!user) return '';
  if (user.entityName)
    return (user.entityName ?? '').substring(0, 1).toLocaleUpperCase();

  return [
    (user.firstName ?? '').substring(0, 1).toLocaleUpperCase(),
    (user.lastName ?? '').substring(0, 1).toLocaleUpperCase(),
  ].join('');
};

export const validateForm = (
  rules: ValidateRule[],
  formObject: Record<string, unknown>,
): boolean => {
  let validate = false;
  for (const attr of rules) {
    // implement rules that you need to compare
    if (attr.rule === 'SHOULD_EXIST') {
      if (attr.pattern) {
        const destinationValues = jp.query(formObject, attr?.pattern)[0]; // use destination  value to check
        validate = !!destinationValues;
      } else {
        const attributeValue = formObject[attr.key];
        const isExist = Object.hasOwn(formObject, attr.key);
        validate = isExist && attributeValue !== '';
      }
      if (!validate) {
        return validate;
      }
    } else if (attr.rule === 'HAS_ANY_TRANSLATION') {
      // Validate that at least one language has a non-empty value
      const translatedValue = formObject[attr.key];
      validate = hasAnyTranslation(translatedValue);
      if (!validate) {
        return validate;
      }
    }
  }
  return validate;
};

export const formatPhoneNumber = (phone: string | undefined): string => {
  if (!phone) return '';
  if (phone.length < 10) return phone;
  if (phone.length === 10)
    return parsePhoneNumber(phone, 'CA').formatInternational();

  let sanitizedNumber = phone.replace(/[^\d]/g, '');

  sanitizedNumber = sanitizedNumber.slice(0, 15);
  const phoneNumberWithPlus = `+${sanitizedNumber}`;
  try {
    const parsedPhoneNumber = parsePhoneNumber(phoneNumberWithPlus);
    return parsedPhoneNumber.formatInternational();
  } catch {
    return phoneNumberWithPlus;
  }
};

export const formatPhoneNumberForInput = (
  phone: string | undefined,
): string => {
  if (!phone) return '';
  let sanitizedNumber = phone.replace(/[^\d]/g, '');

  sanitizedNumber = sanitizedNumber.slice(0, 15);
  const phoneNumberWithPlus = `+${sanitizedNumber}`;
  try {
    const parsedPhoneNumber = parsePhoneNumber(phoneNumberWithPlus);
    return parsedPhoneNumber.formatInternational();
  } catch {
    return phoneNumberWithPlus;
  }
};

export const entityName = (entity: any, full = false) => {
  if (entity?.firstName || entity?.lastName) {
    return [
      entity.firstName,
      full ? entity.middleName : undefined,
      entity.lastName,
    ]
      .filter(Boolean)
      .join(' ');
  }
  return entity?.entityName;
};

export type EntityInitialsSource = {
  firstName?: string | null;
  lastName?: string | null;
  entityName?: string | null;
  primaryEmail?: string | null;
};

export const entityInitials = (entity?: EntityInitialsSource) => {
  if (entity?.firstName || entity?.lastName)
    return `${(entity?.firstName?.toUpperCase() ?? '')[0] ?? ''}${(entity?.lastName?.toUpperCase() ?? '')[0] ?? ''}`;
  if (entity?.entityName) return entity.entityName.toUpperCase()[0];
  return (entity?.primaryEmail?.toUpperCase() ?? '')[0];
};

export const entityNameWithGreeting = (
  greeting: string,
  firstName?: string,
) => {
  if (firstName) return `${greeting}, ${firstName}`;
  return greeting;
};

export const entityLink = (entity: Pick<User, 'id' | 'type'>): string => {
  if (entity.type === 'INDIVIDUAL') {
    return `/clients/${entity.id}`;
  }
  return `/nonIndividualClients/${entity.id}`;
};

export function isValidFlinksUrl(
  origin: string,
  externalIframeUrl?: string,
): boolean {
  const defaultFlinksOrigin = 'https://onevest9163.connect.flinks.dev';
  const defaultFinAgOrigin = 'https://onevest-iframe.private.fin.ag';

  // 1. Check if e.origin is the Flinks URL
  if (origin === defaultFlinksOrigin || origin === defaultFinAgOrigin) {
    return true;
  }

  if (!externalIframeUrl) return false;

  // 3. Extract everything up to and including flinks.dev or private.fin.ag
  //    e.g. https://sub.domain.connect.flinks.dev/path -> https://sub.domain.connect.flinks.dev
  //         https://whatever.private.fin.ag/anything   -> https://whatever.private.fin.ag
  const domainPattern = /^(https:\/\/[^/]*(?:flinks\.dev|private\.fin\.ag))/;
  const match = externalIframeUrl.match(domainPattern);

  // If we fail to extract a valid domain from externalIframeUrl return false,
  if (!match) return false;

  const baseDomain = match[1]; // e.g. 'https://onevest-iframe.private.fin.ag'

  // 4. Check if e.origin matches that exact base domain
  return origin === baseDomain;
}

export const validateUrl = (url: string): boolean =>
  isValidUrl(url) || isValidEmail(url);

/**
 * Checks if a string contains a valid-looking email address.
 *
 * @param email Email address to validate
 * @returns {boolean}
 */
export const isValidEmail = (email: string | undefined): boolean => {
  if (!email) return false;
  const regex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,24}$/i;
  return regex.test(email);
};

export const isValidUrl = (url: string): boolean => {
  const regex =
    /^(http(s)?:\/\/.)[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)$/;
  return regex.test(url);
};

/**
 * Checks if a string is a valid `mailto:` link (e.g. `mailto:support@example.com`).
 */
export const isValidMailtoUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'mailto:') {
      return false;
    }
    const email = decodeURIComponent(parsed.pathname);
    return isValidEmail(email);
  } catch {
    return false;
  }
};

/**
 * Validates a URL that may be an http(s) link, a plain email address,
 * or a `mailto:` link. Suitable for any contact/resource URL field.
 */
export const validateContactUrl = (url: string | null | undefined): boolean =>
  !!url && (validateUrl(url) || isValidMailtoUrl(url));

export const parseLink = (url: string) => {
  try {
    return new URL(url).href;
  } catch {
    return `https://${url}`;
  }
};

const getCustomFieldSelectedOptionsAsText = (
  translateBackendString: (translatedObject: unknown) => string,
  customFieldValue: CustomFieldValue,
) => {
  const selectedOptions = customFieldValue?.selectedOptions;

  if (selectedOptions) {
    return selectedOptions
      .map((option) => translateBackendString(option.displayText))
      .join(', ');
  }

  return customFieldValue.value;
};

export const getCustomFieldValueByKey = (
  translateBackendString: (translatedObject: unknown) => string,
  key: string,
  customFieldValues: CustomFieldValue[],
  t?: TFunction,
  showDefaultVal?: boolean,
): any => {
  const customFieldValue = customFieldValues.find(
    (elem) => elem.key.toLowerCase() === key.toLowerCase(),
  );

  if (customFieldValue?.customField?.format) {
    const { format } = customFieldValue.customField;
    switch (format) {
      case CustomFieldFormats.SimpleText:
        return customFieldValue?.value ?? '-';
      case CustomFieldFormats.Currency:
        return formatMoneyValue(customFieldValue?.value ?? 0, '$', 2, false);
      case CustomFieldFormats.Number:
        return customFieldValue?.value ?? 0;
      case CustomFieldFormats.ToggleSwitch:
      case CustomFieldFormats.RadioButton:
        return typeof customFieldValue?.value === 'boolean' && t
          ? customFieldValue?.value
            ? t('shared:yes')
            : t('shared:no')
          : '-';
      case CustomFieldFormats.DropdownList:
        return getCustomFieldSelectedOptionsAsText(
          translateBackendString,
          customFieldValue,
        );
      case CustomFieldFormats.Custom:
        return '-';
      default:
        return customFieldValue.value;
    }
  }
  if (customFieldValue?.value) {
    return customFieldValue.value;
  }
  return showDefaultVal ? '-' : undefined;
};

export const getCustomFieldTypeInfo = (
  customFieldType?: CustomFieldTypes,
): { isNumber: boolean; isDate: boolean } => {
  const isNumber =
    !!customFieldType &&
    [CustomFieldTypes.Decimal, CustomFieldTypes.Integer].includes(
      customFieldType,
    );
  const isDate = !!customFieldType && customFieldType === CustomFieldTypes.Date;

  return { isNumber, isDate };
};

export const isValidMongoId = (id: string) => {
  // Check if id is a stringified Bson Object
  if (id.length !== 24) return false;
  if (!/^[0-9a-fA-F]{24}$/.test(id)) {
    return false;
  }
  // Extract and validate the timestamp portion
  const timestamp = id.substring(0, 8);
  const date = new Date(parseInt(timestamp, 16) * 1000);
  if (date.getTime() <= 0 || date.getTime() >= Date.now()) {
    return false;
  }

  const machineIdentifier = id.substring(8, 14);
  const processIdentifier = id.substring(14, 18);
  const counter = id.substring(18, 24);
  // Ensure that the machine identifier, process identifier, and counter are hexadecimal
  const hexRegex = /^[a-f\d]+$/i;
  if (
    !hexRegex.test(machineIdentifier) ||
    !hexRegex.test(processIdentifier) ||
    !hexRegex.test(counter)
  ) {
    return false;
  }
  return true;
};

export const getBookCostCents = (
  holding: Holding,
  bookValueType?: BookValueType,
): number => {
  const hasBookValueType =
    bookValueType &&
    !isNull(holding[bookValueType as BookValueType]) &&
    !!holding[bookValueType as BookValueType];
  let value = 0;
  if (
    isNull(holding.financialProduct?.isCash) ||
    !holding.financialProduct?.isCash
  ) {
    value = (holding.adjustedCostBaseCents ?? 0) * (holding.quantity ?? 0);
  }
  if (hasBookValueType) {
    value = holding[bookValueType as BookValueType] ?? 0;
  }
  return value;
};

export const truncateNumber = (number: string): string => {
  if (number.length <= 4) return number; // If the number has 4 or fewer digits, don't truncate.

  const visiblePart = number.slice(-4);
  const maskedPart = '*'.repeat(number.length - 4);
  return maskedPart + visiblePart;
};

export const phoneValidator = (
  value: string,
  countryCode?: CountryCode,
  applicableCountries?: CountryCodes[],
): boolean => {
  if (!value) return false;
  try {
    const phoneNumber = parsePhoneNumberFromString(value, {
      defaultCountry: countryCode,
    });
    if (
      !applicableCountries
        ?.map((c) => c.toLowerCase())
        ?.includes(phoneNumber?.country?.toLowerCase() ?? '')
    ) {
      if (!value) return false;
    }
    const checkPossibility = ['test', 'staging', 'development'].includes(
      process.env.REACT_APP_ENV ?? '',
    );
    /* isPossible is only checking the number of digits, not the validity of the number */
    /* For example, 1234567890 is possible in US, but it is not valid */
    /* this is only for development and staging environments */
    if (checkPossibility) {
      return phoneNumber
        ? phoneNumber.isValid() || phoneNumber.isPossible()
        : false;
    } else {
      return phoneNumber ? phoneNumber.isValid() : false;
    }
  } catch {
    return false;
  }
};

export const filterBankAccountsByCurrency = (
  bankAccounts: BankAccount[],
  accountCurrencies?: (string | null)[] | null,
  currency?: string,
): BankAccount[] => {
  if (currency) {
    return bankAccounts.filter((bankAccount: BankAccount) =>
      bankAccount.currency ? bankAccount.currency === currency : true,
    );
  }
  if (accountCurrencies?.length) {
    return bankAccounts.filter((bankAccount: BankAccount) =>
      bankAccount.currency
        ? accountCurrencies.includes(bankAccount.currency)
        : true,
    );
  }
  return bankAccounts;
};

export const convertToCents = (value: string | number): number | null => {
  if (!Number.isFinite(Number(value))) {
    return null;
  }
  if (Number(value) % 1 === 0) {
    return Number(value);
  }
  return dollars2cents(Number(value));
};

export const isValidHexColor = (color: string) =>
  /^#([0-9A-F]{3}([0-9a-fA-F])?|[0-9A-F]{6}([0-9a-fA-F]{2})?)$/i.test(color);

const booleanOperators = ['isTrue', 'isFalse', 'isEmpty', 'isNotEmpty'];

export const validateRule = (rule: PageConfigurationRuleGroup) => {
  const rules = rule?.rules;
  const hasNested = rules != null && rules.length > 0;
  const hasEmptyRules = rules != null && rules.length === 0;
  if (hasNested) {
    const nestedValid = rules.every((r) => validateRule(r));
    if (!nestedValid) return false;
  }
  if (hasEmptyRules) return false;

  if (rule.operator && !rule.value && !booleanOperators.includes(rule.operator))
    return false;
  return true;
};
